import type { WeekDateRangeResponse } from '../../api/generated';
import { hasWeekStarted } from '../../utils/weekClassifier';
import { collectEditableSeries } from '../../utils/ganttUtils';
import type { GanttGroup, GanttSeries } from './types';

// The scopes a treatment plan removal can apply to inside one year-view row:
// - 'plan'    → only the plan the menu was opened from
// - 'onwards' → that plan and every plan starting in a later week
// - 'row'     → every plan in the row, the same set the row menu takes
export type RemovePlansScope = 'plan' | 'onwards' | 'row';

/**
 * What a removal was launched from, and so what it can be scoped against.
 * - 'plan' → a plan's own menu; the anchor week is what the picker labels read
 * - 'row'  → the row label's menu, which names no single plan and so no week
 *
 * One union rather than a plan id beside an optional week: only the picker
 * labels ever read `selectedStartWeek`, so the row variant has no honest value
 * to put there, and a placeholder would be a number a later caller would be
 * right to trust and wrong to.
 */
export type RemovalAnchor =
  | { kind: 'plan'; treatmentPlanId: string; selectedStartWeek: number }
  | { kind: 'row' };

// The picker opens on 'onwards': a planner who changed a template wants the
// selected plan and everything after it gone so the roll-out can be redone,
// which is the case this whole flow exists for.
export const DEFAULT_REMOVE_PLANS_SCOPE: RemovePlansScope = 'onwards';

// One plan leaves nothing to choose between, so the scope picker only appears
// from this many *placed* plans up. Placeholders do not count: they have no week
// and no visible row, so a row holding one real plan and one legacy empty plan
// still offers no choice.
export const MIN_PLANS_FOR_SCOPE_CHOICE = 2;

export interface PlansToRemove {
  /** Every plan removed, placeholders included. This is the delete payload. */
  treatmentPlanIds: string[];
  /** Distinct start weeks of the placed plans, ascending. */
  startWeeks: number[];
  /**
   * How many of the removed plans the planner can actually see. Excludes
   * placeholders, which have no week and no row of their own. This is what the
   * summary must count: `treatmentPlanIds.length` would say "2 plans removed"
   * for one real plan plus a phantom, and name only one week beside it.
   */
  placedPlanCount: number;
}

// The lowest week holding an intervention group. Null for an edit-time
// placeholder plan, which has no weeks and so no position in the row.
export function getSeriesStartWeek(series: GanttSeries): number | null {
  let start: number | null = null;
  for (const group of series.groups) {
    if (start === null || group.weekNumber < start) start = group.weekNumber;
  }
  return start;
}

// Placeholders sort last: they have no position in the row, and putting them
// at the front would make the id order disagree with the weeks on screen.
const START_WEEK_SORT_FALLBACK = Number.MAX_SAFE_INTEGER;

function byStartWeek(a: GanttSeries, b: GanttSeries): number {
  return (
    (getSeriesStartWeek(a) ?? START_WEEK_SORT_FALLBACK) -
    (getSeriesStartWeek(b) ?? START_WEEK_SORT_FALLBACK)
  );
}

/** Plans with a week, so plans the planner can see. */
export function countPlacedPlans(series: GanttSeries[]): number {
  return series.filter((s) => getSeriesStartWeek(s) !== null).length;
}

function collectDistinctStartWeeks(series: GanttSeries[]): number[] {
  const weeks = new Set<number>();
  for (const s of series) {
    const start = getSeriesStartWeek(s);
    if (start !== null) weeks.add(start);
  }
  return [...weeks].sort((a, b) => a - b);
}

function summarisePlans(targets: GanttSeries[]): PlansToRemove {
  return {
    treatmentPlanIds: [...targets].sort(byStartWeek).map((s) => s.treatmentPlanId),
    startWeeks: collectDistinctStartWeeks(targets),
    placedPlanCount: countPlacedPlans(targets)
  };
}

// Series order in a row follows the server array. Roll-out fills that in week
// order, but moving a plan with the week arrows can leave it out of order, so
// "following" is measured on start weeks and never on position. A plan starting
// in the same week as the selected one runs alongside it rather than after it,
// so it is left alone.
export function computePlansToRemove(
  group: GanttGroup,
  anchor: RemovalAnchor,
  scope: RemovePlansScope
): PlansToRemove {
  const editable = collectEditableSeries(group);

  // A row anchor has no plan to narrow from: the menu it came from named the
  // whole row before the dialog opened, so every plan in it is in range and the
  // scope has nothing left to say. The row entry point locks the scope to 'row'
  // for the same reason; this makes the outcome the same either way.
  if (anchor.kind === 'row') return summarisePlans(editable);

  const selected = editable.find((s) => s.treatmentPlanId === anchor.treatmentPlanId);
  if (!selected) return summarisePlans([]);

  const selectedStart = getSeriesStartWeek(selected);

  const targets = editable.filter((s) => {
    if (s.treatmentPlanId === anchor.treatmentPlanId) return true;
    if (scope === 'row') return true;
    if (scope === 'plan' || selectedStart === null) return false;
    const start = getSeriesStartWeek(s);
    return start !== null && start > selectedStart;
  });

  return summarisePlans(targets);
}

// Weeks among the plans being removed that have already started, ascending.
//
// `hasWeekStarted` mirrors the backend's `IsoWeekDateRangeUtil.hasWeekStarted`,
// so the current week counts from Monday onwards, not once it has fully elapsed.
// Removing such a plan is allowed, and deliberately so, because a planner has to
// be able to make the year plan match what actually happened. But it takes the
// week's history with it, which is what the dialog has to say out loud before it
// is allowed to happen.
//
// This was written against a backend that then rejected the save with 400, so
// the warning was shown and the removal failed anyway. REH-358 fixed that side,
// which promotes the warning from a second line of defence to the only one on
// this path. Note the scope: it covers removing a plan, not every destructive
// edit to a week that has run. Dropping an intervention group from such a week,
// or changing its capacity, is now accepted with no acknowledgement anywhere.
// Backend ADR 0007 takes that as an accepted consequence.
//
// This is the input to a warning that guards irreversible data loss, so where a
// week cannot be resolved it errs towards warning rather than towards silence.
// See the unresolved-week handling below.
export function collectAlreadyStartedWeeks(
  group: GanttGroup,
  treatmentPlanIds: string[],
  weeks: WeekDateRangeResponse[],
  today: Date
): number[] {
  const targetIds = new Set(treatmentPlanIds);
  const weekByNumber = new Map(weeks.map((w) => [w.weekNumber, w]));
  const lastKnownWeek = weeks.reduce((max, w) => Math.max(max, w.weekNumber), 0);
  const started = new Set<number>();

  for (const series of group.series) {
    if (!targetIds.has(series.treatmentPlanId)) continue;
    for (const interventionGroup of series.groups) {
      const weekNumber = interventionGroup.weekNumber;
      const week = weekByNumber.get(weekNumber);

      if (week) {
        if (hasWeekStarted(week, today)) started.add(weekNumber);
        continue;
      }

      // No date range for this week. Past the last week the year knows about it
      // can only be a plan overrunning year end, which is still ahead: roll-out
      // cuts on a plan's start week, not its end, so a multi-week plan starting
      // late legitimately spills over. Anything else is a mismatch we cannot
      // resolve, and treating an unresolvable week as "not started" would drop
      // the warning silently, which is the one outcome this must never produce.
      if (weeks.length === 0 || weekNumber <= lastKnownWeek) started.add(weekNumber);
    }
  }

  return [...started].sort((a, b) => a - b);
}
